import axios from "@utils/axios-instance";
import { TUser } from "@/models/user.model";
import { TCreateLeadPayload, TLead, TLeadResponse, TLeadsFilters, TLeadStatus } from "@/models/leads.model";
import { TLeadMergeRequest, TLeadMergeResponse } from "@/models/lead-merge.model";
import { TCreateLeadTaskPayload, TLeadTask, TUpdateTaskPayload } from "@/models/tasks.model";

// *** CRUD STARTS *** //

const createLead = async (data: TCreateLeadPayload): Promise<TLead> => {
  const response = await axios.post(`/lead/`, data);
  return response.data;
};

const getAll = async (filters: TLeadsFilters): Promise<TLeadResponse> => {
  filters = Object.fromEntries(Object.entries(filters).filter(([, val]) => Boolean(val)));
  const queryString = new URLSearchParams(filters as Record<string, string>).toString();
  const response = await axios.get(`/lead?${queryString}`);
  return response.data;
};

const getById = async (leadId: number): Promise<TLead> => {
  const response = await axios.get(`/lead/${leadId}`);
  return response.data;
};

const getByEmail = async (email: string): Promise<TLead> => {
  const response = await axios.get(`/lead/search?email=${encodeURIComponent(email)}`);
  return response.data;
};


const update = async (leadId: number, data: Partial<Omit<TLead, "id">>): Promise<TUser> => {
  const response = await axios.patch(`/lead/${leadId}`, data);
  return response.data;
};

export const deleteLead = async (leadId: number): Promise<void> => {
  await axios.delete(`/lead/${leadId}`);
};

const mergeLeads = async (payload: TLeadMergeRequest): Promise<TLeadMergeResponse> => {
  const response = await axios.post("/lead/merge", payload);
  return response.data;
};

// *** CRUD ENDS *** //

export const order = async (order: number[]): Promise<void> => {
  await axios.patch(`/lead/order`, order);
};

export const exportLeads = async (): Promise<string> => {
  const response = await axios.get(`/lead/export`);
  return response.data;
};

// LEAD TASKS
export const getLeadTasks = async (leadId: number): Promise<TLeadTask[]> => {
  const response = await axios.get(`/lead/${leadId}/tasks`);
  return response.data.data;
};

export const saveLeadTask = async (leadId: number, task: Pick<TLeadTask, "task" | "isComplete">): Promise<TLeadTask & { id: number }> => {
  const response = await axios.post(`/lead/${leadId}/tasks`, task);
  return response.data;
};

export const createLeadTask = async (leadId: number, data: TCreateLeadTaskPayload): Promise<TLeadTask> => {
  const assignedToId = data.assignedToId ? Number(data.assignedToId) : undefined;
  const response = await axios.post(`/lead/${leadId}/tasks`, { ...data, isComplete: false, assignedToId });
  return response.data;
};

export const updateLeadTaskFull = async ({
  leadId,
  taskId,
  data,
}: { leadId: number; taskId: number; data: TUpdateTaskPayload }): Promise<TLeadTask> => {
  const response = await axios.patch(`/lead/${leadId}/tasks/${taskId}`, data);
  return response.data;
};

export const removeLeadTask = async ({ leadId, taskId }: { leadId: number; taskId: number }): Promise<void> => {
  await axios.delete(`/lead/${leadId}/tasks/${taskId}`);
};

export const toggleLeadTaskStatus = async ({
  leadId,
  taskId,
  isComplete,
}: {
  leadId: number;
  taskId: number;
  isComplete: boolean;
}): Promise<void> => {
  await axios.patch(`/lead/${leadId}/tasks/${taskId}`, { isComplete });
};

export const updateLeadTask = async ({
  leadId,
  taskId,
  task,
}: {
  leadId: number;
  taskId: number;
  task: string;
}): Promise<void> => {
  await axios.patch(`/lead/${leadId}/tasks/${taskId}`, { task });
};

// LEAD STAGES
export const getLeadStatusList = async (): Promise<TLeadStatus[]> => {
  const response = await axios.get(`/lead/status`);
  return response.data;
};

export const saveLeadStatus = async (data: Pick<TLeadStatus, "name" | "color">): Promise<TLeadStatus> => {
  const response = await axios.post(`/lead/status`, data);
  return response.data;
};

export const updateLeadStatus = async (data: TLeadStatus): Promise<TLeadStatus> => {
  const { id, color, name } = data;
  const response = await axios.patch(`/lead/status/${id}`, { color, name });
  return response.data;
};

export const deleteLeadStatus = async (id: number, newStatusId?: number): Promise<void> => {
  const payload = newStatusId ? { data: { newStatusId } } : undefined;
  await axios.delete(`/lead/status/${id}`, payload);
};

export default {
  createLead,
  getAll,
  getById,
  getByEmail,
  update,
  deleteLead,
  mergeLeads,
  order,
  getLeadStatusList,
  updateLeadStatus,
  saveLeadStatus,
  deleteLeadStatus,
  getLeadTasks,
  saveLeadTask,
  createLeadTask,
  removeLeadTask,
  toggleLeadTaskStatus,
  updateLeadTask,
  updateLeadTaskFull,
};
